import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import contentService from '../services/contentService'
import Loading from '../components/Loading'

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 5000)
    return () => clearTimeout(t)
  }, [onClose])
  return (
    <div style={{
      position: 'fixed', top: 20, right: 20, zIndex: 9999,
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '12px 18px', borderRadius: 'var(--g-radius-md)',
      background: type === 'success' ? '#f0fdf4' : '#fef2f2',
      border: `1px solid ${type === 'success' ? '#86efac' : '#fca5a5'}`,
      color: type === 'success' ? '#166534' : '#991b1b',
      boxShadow: 'var(--g-shadow-md)',
      fontSize: 'var(--g-font-size-sm)', fontWeight: 600,
      maxWidth: 360,
    }}>
      <i className={`bi ${type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'}`}
         style={{ fontSize: 16, flexShrink: 0 }}></i>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.6, padding: 0, marginLeft: 4 }}>
        <i className="bi bi-x" style={{ fontSize: 18 }}></i>
      </button>
    </div>
  )
}

// ── Badge estado ──────────────────────────────────────────────────────────────
function StatusBadge({ conectado, label }) {
  return (
    <span className={`g-badge ${conectado ? 'g-badge-success' : 'g-badge-neutral'}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: conectado ? 'var(--g-lima)' : 'var(--g-text-muted)',
        display: 'inline-block',
      }}></span>
      {label || (conectado ? 'Conectado' : 'No conectado')}
    </span>
  )
}

// ── Card cuenta TikTok ───────────────────────────────────────────────────────
function TikTokAccountCard({ cuenta, onSetPrimary, onDesconectar, actuando }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 16px',
      background: 'var(--g-fondo)',
      borderRadius: 'var(--g-radius-md)',
      border: `1px solid ${cuenta.is_primary ? 'var(--g-lima)' : 'var(--g-border)'}`,
    }}>
      {cuenta.avatar_url ? (
        <img src={cuenta.avatar_url} alt=""
          style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
          onError={e => { e.target.style.display = 'none' }} />
      ) : (
        <div style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          background: '#000',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <i className="bi bi-tiktok" style={{ color: '#fff', fontSize: 16 }}></i>
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 'var(--g-font-size-md)', color: 'var(--g-dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            @{cuenta.username}
          </p>
          {cuenta.is_primary && (
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--g-dark)', background: 'var(--g-lima)', padding: '1px 6px', borderRadius: 999 }}>
              Principal
            </span>
          )}
        </div>
        {cuenta.display_name && (
          <p style={{ margin: 0, fontSize: 'var(--g-font-size-xs)', color: 'var(--g-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {cuenta.display_name}
          </p>
        )}
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <p style={{ margin: 0, fontWeight: 800, fontSize: 'var(--g-font-size-lg)', color: 'var(--g-dark)' }}>
          {cuenta.followers_count != null ? cuenta.followers_count.toLocaleString('es-CL') : '—'}
        </p>
        <p style={{ margin: 0, fontSize: 'var(--g-font-size-xs)', color: 'var(--g-text-muted)' }}>seguidores</p>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {!cuenta.is_primary && (
          <button onClick={() => onSetPrimary(cuenta.id)} disabled={actuando} className="g-btn g-btn-secondary"
            style={{ fontSize: 11, padding: '5px 10px' }} title="Establecer como principal">
            <i className="bi bi-star"></i>
          </button>
        )}
        <button onClick={() => onDesconectar(cuenta.id, cuenta.username)} disabled={actuando} className="g-btn g-btn-danger"
          style={{ fontSize: 11, padding: '5px 10px' }} title="Desconectar">
          <i className="bi bi-x-lg"></i>
        </button>
      </div>
    </div>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ icon, title, desc, action }) {
  return (
    <div className="g-empty">
      <i className={`bi ${icon} g-empty-icon`}></i>
      <p className="g-empty-title">{title}</p>
      {desc && <p className="g-empty-desc">{desc}</p>}
      {action}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function CuentasConectadasTikTokPage() {
  const [searchParams] = useSearchParams()

  const [cuentas, setCuentas]           = useState([])
  const [loading, setLoading]           = useState(true)
  const [conectando, setConectando]     = useState(false)
  const [sincronizando, setSincronizando] = useState(false)
  const [actuandoId, setActuandoId]     = useState(null)

  const [toast, setToast] = useState(null)
  const showToast = (message, type = 'success') => setToast({ message, type })

  const cargarCuentas = useCallback(async () => {
    try {
      const { data } = await contentService.getMyTikTokAccount()
      if (Array.isArray(data.accounts)) {
        setCuentas(data.accounts)
      } else if (data && data.id) {
        setCuentas([data])
      } else {
        setCuentas([])
      }
    } catch {
      setCuentas([])
    }
  }, [])

  useEffect(() => {
    if (searchParams.get('tiktok_connected') === '1') showToast('Cuenta de TikTok conectada correctamente ✓')
    if (searchParams.get('tiktok_error'))             showToast('Error al conectar con TikTok.', 'error')
    setLoading(true)
    cargarCuentas().finally(() => setLoading(false))
  }, [cargarCuentas, searchParams])

  // Refresca la lista cuando la ventana recupera el foco (tras cerrar el popup de OAuth)
  useEffect(() => {
    const onFocus = () => cargarCuentas()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [cargarCuentas])

  const handleConectar = async () => {
    setConectando(true)
    try {
      const { data } = await contentService.getTikTokOAuthUrl()
      const url = data.auth_url || data.url
      if (!url) throw new Error('sin url')
      window.open(url, 'tiktok_oauth', 'width=520,height=720,menubar=no,toolbar=no,location=no')
    } catch {
      showToast('No se pudo obtener la URL de autenticación de TikTok', 'error')
    } finally {
      setConectando(false)
    }
  }

  const handleSincronizar = async () => {
    setSincronizando(true)
    try {
      await contentService.refreshTikTokAccount()
      await cargarCuentas()
      showToast('Cuenta TikTok sincronizada')
    } catch {
      showToast('Error al sincronizar TikTok', 'error')
    } finally {
      setSincronizando(false)
    }
  }

  const handleSetPrimary = async (accountId) => {
    setActuandoId(accountId)
    try {
      await contentService.setPrimaryTikTokAccount(accountId)
      await cargarCuentas()
      showToast('Cuenta principal actualizada')
    } catch {
      showToast('Error al establecer cuenta principal', 'error')
    } finally {
      setActuandoId(null)
    }
  }

  const handleDesconectar = async (accountId, username) => {
    if (!window.confirm(`¿Desconectar @${username}? Se eliminará la vinculación con esta cuenta de TikTok.`)) return
    setActuandoId(accountId)
    try {
      await contentService.disconnectTikTokAccount(accountId)
      setCuentas(prev => prev.filter(c => c.id !== accountId))
      showToast('Cuenta de TikTok desconectada')
    } catch {
      showToast('Error al desconectar', 'error')
    } finally {
      setActuandoId(null)
    }
  }

  const conectado = cuentas.length > 0

  return (
    <div className="g-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="g-page-header">
        <div>
          <h1 className="g-page-title">
            <i className="bi bi-tiktok" style={{ marginRight: 10, color: 'var(--g-lima)' }}></i>
            Cuentas Conectadas — TikTok
          </h1>
          <p className="g-page-subtitle">Administra las cuentas de TikTok vinculadas a través de TikTok for Developers.</p>
        </div>
        {conectado && (
          <a href="/tiktok-analytics" className="g-btn g-btn-secondary">
            <i className="bi bi-bar-chart-fill"></i>Ver Analytics
          </a>
        )}
      </div>

      <div style={{ maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* ── Panel TikTok OAuth ───────────────────────────────────────── */}
        <div className="g-card">
          <div className="g-card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 'var(--g-radius-md)',
                background: '#000',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <i className="bi bi-tiktok" style={{ color: '#fff', fontSize: 20 }}></i>
              </div>
              <div>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 'var(--g-font-size-md)', color: 'var(--g-dark)' }}>
                  TikTok for Developers
                </p>
                <p style={{ margin: 0, fontSize: 'var(--g-font-size-xs)', color: 'var(--g-text-muted)' }}>
                  App: Geves developer
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <StatusBadge conectado={conectado} />
              <button onClick={handleConectar} disabled={conectando} className="g-btn g-btn-primary"
                style={{ fontSize: 'var(--g-font-size-xs)', padding: '6px 14px', background: '#000', borderColor: '#000', color: '#fff' }}>
                {conectando
                  ? <span className="g-spinner" style={{ width: 12, height: 12, borderWidth: 2, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }}></span>
                  : <i className="bi bi-tiktok"></i>}
                {conectado ? 'Conectar otra cuenta' : 'Conectar con TikTok'}
              </button>
            </div>
          </div>

          {/* Permisos */}
          <div className="g-card-body" style={{ paddingTop: 14, paddingBottom: 14 }}>
            <p style={{ margin: '0 0 8px', fontSize: 'var(--g-font-size-xs)', fontWeight: 700, color: 'var(--g-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              <i className="bi bi-shield-check-fill" style={{ marginRight: 5 }}></i>Permisos utilizados
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {['user.info.basic', 'user.info.profile', 'user.info.stats', 'video.list'].map(p => (
                <code key={p} style={{
                  padding: '3px 8px', borderRadius: 'var(--g-radius-sm)',
                  background: 'var(--g-fondo)', border: '1px solid var(--g-border)',
                  fontSize: 11, color: 'var(--g-text-sec)', fontFamily: 'monospace',
                }}>{p}</code>
              ))}
            </div>
          </div>
        </div>

        {/* ── Pantalla de conexión (sin cuentas) ──────────────────────── */}
        {!loading && !conectado && (
          <div className="g-card">
            <div className="g-card-body" style={{ textAlign: 'center', padding: '48px 24px' }}>
              <div style={{
                width: 72, height: 72, borderRadius: 'var(--g-radius-xl)',
                background: '#000',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px', boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
              }}>
                <i className="bi bi-tiktok" style={{ color: '#fff', fontSize: 32 }}></i>
              </div>
              <p style={{ margin: '0 0 6px', fontWeight: 800, fontSize: 'var(--g-font-size-xl)', color: 'var(--g-dark)' }}>
                Conecta tu cuenta de TikTok
              </p>
              <p style={{ margin: '0 0 24px', fontSize: 'var(--g-font-size-sm)', color: 'var(--g-text-muted)', maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
                Autoriza el acceso a tu cuenta de TikTok para ver seguidores, videos y estadísticas directamente aquí.
              </p>
              <button onClick={handleConectar} disabled={conectando} className="g-btn g-btn-primary"
                style={{ fontSize: 'var(--g-font-size-md)', padding: '10px 28px', background: '#000', border: 'none', color: '#fff' }}>
                {conectando
                  ? <span className="g-spinner" style={{ width: 14, height: 14, borderWidth: 2, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }}></span>
                  : <i className="bi bi-tiktok"></i>}
                Continuar con TikTok
              </button>
            </div>
          </div>
        )}

        {/* ── Loading inicial ───────────────────────────────────────────── */}
        {loading && (
          <Loading fullScreen={false} mensaje="Cargando" />
        )}

        {/* ── Cuentas conectadas ───────────────────────────────────────── */}
        {!loading && conectado && (
          <div className="g-card">
            <div className="g-card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 'var(--g-radius-md)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  background: '#000',
                }}>
                  <i className="bi bi-person-video3" style={{ color: '#fff', fontSize: 20 }}></i>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span className="g-card-header-title">Cuentas TikTok</span>
                    <StatusBadge conectado label={`${cuentas.length} conectada${cuentas.length === 1 ? '' : 's'}`} />
                  </div>
                  <p style={{ margin: 0, fontSize: 'var(--g-font-size-xs)', color: 'var(--g-text-muted)', marginTop: 2 }}>
                    Cuentas de TikTok vinculadas a tu usuario
                  </p>
                </div>
              </div>
              <button
                onClick={handleSincronizar}
                disabled={sincronizando}
                className="g-btn g-btn-secondary"
                style={{ fontSize: 'var(--g-font-size-xs)', padding: '6px 14px', flexShrink: 0 }}
              >
                <i className={`bi bi-arrow-clockwise ${sincronizando ? 'spin' : ''}`}></i>
                Sincronizar
              </button>
            </div>
            <div className="g-card-body">
              {cuentas.length === 0 ? (
                <EmptyState
                  icon="bi-tiktok"
                  title="Sin cuentas TikTok"
                  desc="Conecta una cuenta para empezar a ver sus estadísticas."
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {cuentas.map(cuenta => (
                    <TikTokAccountCard
                      key={cuenta.id}
                      cuenta={cuenta}
                      onSetPrimary={handleSetPrimary}
                      onDesconectar={handleDesconectar}
                      actuando={actuandoId === cuenta.id}
                    />
                  ))}
                  {cuentas[0]?.last_synced_at && (
                    <p style={{ margin: '4px 0 0', fontSize: 'var(--g-font-size-xs)', color: 'var(--g-text-muted)' }}>
                      <i className="bi bi-clock" style={{ marginRight: 4 }}></i>
                      Última sincronización: {new Date(cuentas[0].last_synced_at).toLocaleString('es-CL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      <style>{`
        .spin { animation: spin 0.8s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .g-spinner {
          width: 28px; height: 28px;
          border: 3px solid var(--g-border);
          border-top-color: var(--g-lima);
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
          display: inline-block;
        }
        .g-btn-danger {
          background: #fff0f0;
          color: #dc2626;
          border-color: #fca5a5;
        }
        .g-btn-danger:hover:not(:disabled) {
          background: #fee2e2;
          border-color: #f87171;
        }
      `}</style>
    </div>
  )
}
